import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { Pass } from 'three/addons/postprocessing/Pass.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

/** Fear grade: vignette, desaturation, chromatic fringing and film grain driven by tension. */
const FearShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uTime: { value: 0 },
    uFear: { value: 0 },
    uPulse: { value: 0 },
    uRes: { value: new THREE.Vector2(1, 1) },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uFear;
    uniform float uPulse;
    uniform vec2 uRes;
    varying vec2 vUv;

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
    }

    void main() {
      vec2 c = vUv - 0.5;
      float r = length(c * vec2(uRes.x / uRes.y, 1.0)) / (0.5 * uRes.x / uRes.y);
      // Lens chromatic aberration: red and blue pull apart towards the edges
      // (grows with distance from centre), spread over a few taps so the fringe is smooth.
      vec2 ca = c * r * (0.011 + uFear * 0.012 + uPulse * 0.008);
      vec3 col = vec3(0.0);
      for (int i = 0; i < 4; i++) {
        float t = (float(i) + 0.5) / 4.0;
        col.r += texture2D(tDiffuse, vUv + ca * t).r;
        col.g += texture2D(tDiffuse, vUv + ca * (t - 0.5) * 0.3).g;
        col.b += texture2D(tDiffuse, vUv - ca * t).b;
      }
      col *= 0.25;

      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      col = mix(col, vec3(lum) * vec3(0.92, 0.96, 1.06), uFear * 0.55);

      // Filmic grade: cool shadows, warm highlights, a gentle S-curve.
      vec3 tone = mix(vec3(0.9, 0.98, 1.1), vec3(1.08, 1.0, 0.9), smoothstep(0.04, 0.55, lum));
      col *= tone;
      vec3 cl = clamp(col, 0.0, 1.0);
      col = mix(col, cl * cl * (3.0 - 2.0 * cl), 0.28);

      float inner = 0.55 - uFear * 0.32 - uPulse * 0.05;
      float vig = smoothstep(1.15, inner, r);
      col *= mix(0.18, 1.0, vig);

      float g = hash(vUv * uRes + fract(uTime * 7.31) * 113.0) - 0.5;
      col += g * (0.03 + uFear * 0.05);
      gl_FragColor = vec4(col, 1.0);
    }
  `,
};

/**
 * Old CRT set: curved glass, a soft smeared picture with colour bleed and glow,
 * thick scanlines, a faint shadow mask, a rolling band, flicker and glitch
 * bursts. Only the 3D view goes through it: the HTML UI stays crisp on top.
 */
const CrtShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uTime: { value: 0 },
    uFear: { value: 0 },
    uGlitch: { value: 0 },
    uSeed: { value: 0 },
    uRes: { value: new THREE.Vector2(1, 1) },
    uDpr: { value: 1 },
  },
  vertexShader: FearShader.vertexShader,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uFear;
    uniform float uGlitch;
    uniform float uSeed;
    uniform vec2 uRes;
    uniform float uDpr;
    varying vec2 vUv;

    float hash(vec2 p) {
      return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
    }

    vec2 curve(vec2 uv) {
      vec2 c = uv * 2.0 - 1.0;
      c *= 1.0 + vec2(c.y * c.y, c.x * c.x) * vec2(0.03, 0.045);
      return c * 0.5 + 0.5;
    }

    // Soft horizontal smear, the way an old tube blurs along each line.
    vec3 smear(vec2 uv, float spread) {
      vec2 px = vec2(spread / uRes.x, 0.0);
      vec3 c = texture2D(tDiffuse, uv).rgb * 0.62;
      c += texture2D(tDiffuse, uv + px).rgb * 0.14;
      c += texture2D(tDiffuse, uv - px).rgb * 0.14;
      c += texture2D(tDiffuse, uv + px * 2.5).rgb * 0.05;
      c += texture2D(tDiffuse, uv - px * 2.5).rgb * 0.05;
      return c;
    }

    void main() {
      vec2 uv = curve(vUv);
      if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
        gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
        return;
      }

      // --- glitch: torn bands slide sideways, the picture hops, colours split
      float g = uGlitch;
      float frame = floor(uTime * 24.0);
      float band = floor(uv.y * mix(14.0, 40.0, hash(vec2(uSeed, 1.0))));
      float tear = step(1.0 - g * 0.25, hash(vec2(band, frame + uSeed)));
      uv.x += tear * (hash(vec2(band, frame * 1.7)) - 0.5) * 0.035 * g;
      uv.y += g * (hash(vec2(frame, uSeed)) - 0.5) * 0.004;
      // Constant gentle sync wobble, stronger when she's scared.
      uv.x += sin(uv.y * 160.0 + uTime * 1.5) * (0.0003 + uFear * 0.001);
      uv = clamp(uv, 0.0, 1.0);

      float split = 0.6 + uFear * 0.8 + g * 4.0;
      vec2 off = vec2(split / uRes.x, 0.0);
      vec3 col;
      col.r = smear(uv + off, 1.0).r;
      col.g = smear(uv, 1.0).g;
      col.b = smear(uv - off, 1.0).b;

      // Phosphor glow: bright things bleed into their surroundings.
      vec2 gp = 4.0 / uRes;
      vec3 glow = texture2D(tDiffuse, uv + vec2(gp.x, 0.0)).rgb + texture2D(tDiffuse, uv - vec2(gp.x, 0.0)).rgb
        + texture2D(tDiffuse, uv + vec2(0.0, gp.y)).rgb + texture2D(tDiffuse, uv - vec2(0.0, gp.y)).rgb;
      col += max(glow * 0.25 - 0.18, 0.0) * 0.4;

      // Scanlines (one every 4 CSS pixels, following the curved glass).
      // Bright lines swell into the dark gaps, as a real beam does.
      float lines = uRes.y / (4.0 * uDpr);
      float d = abs(fract(uv.y * lines) - 0.5) * 2.0;
      float lum = dot(col, vec3(0.299, 0.587, 0.114));
      float beam = exp(-pow(d / mix(0.5, 0.95, clamp(lum * 1.4, 0.0, 1.0)), 2.0) * 2.2);
      col *= mix(0.66, 1.06, beam);

      // Faint aperture-grille mask.
      float m = mod(gl_FragCoord.x, 3.0);
      col *= vec3(m < 1.0 ? 1.0 : 0.95, (m >= 1.0 && m < 2.0) ? 1.0 : 0.95, m >= 2.0 ? 1.0 : 0.95);

      // Rolling bright band, mains flicker, and snow during glitches.
      float roll = fract(uv.y * 0.5 - uTime * 0.02);
      col *= 1.0 + smoothstep(0.0, 0.05, roll) * smoothstep(0.1, 0.05, roll) * 0.04 + sin(uTime * 110.0) * 0.008;
      float snow = hash(gl_FragCoord.xy + frame * 13.1);
      col += (snow - 0.5) * (0.02 + g * 0.07);
      // Bright hairline streaks across torn bands.
      col += tear * step(0.99, hash(vec2(floor(uv.y * uRes.y * 0.5), frame))) * 0.15 * g;

      // Glass edges fall off; lift brightness to make up for the scanline layer.
      vec2 e = uv * (1.0 - uv);
      col *= pow(clamp(e.x * e.y * 30.0, 0.0, 1.0), 0.25);
      gl_FragColor = vec4(col * 1.14, 1.0);
    }
  `,
};

/**
 * Far-field depth of field: things well past her go soft, with bright lamps
 * opening into round bokeh discs. Reads the scene depth the RenderPass just
 * wrote, so it must sit straight after it.
 */
const BokehShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    tDepth: { value: null as THREE.Texture | null },
    uNear: { value: 0.1 },
    uFar: { value: 600 },
    uFocus: { value: 6 },
    uFarStart: { value: 9 },
    uFarFull: { value: 45 },
    uMaxRadius: { value: 5 },
    uRes: { value: new THREE.Vector2(1, 1) },
  },
  vertexShader: FearShader.vertexShader,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform sampler2D tDepth;
    uniform float uNear;
    uniform float uFar;
    uniform float uFocus;
    uniform float uFarStart;
    uniform float uFarFull;
    uniform float uMaxRadius;
    uniform vec2 uRes;
    varying vec2 vUv;

    float viewZ(vec2 uv) {
      float d = texture2D(tDepth, uv).x;
      return uNear * uFar / (uFar - d * (uFar - uNear));
    }

    // 0 = sharp, 1 = fully blurred. Only the far side blurs.
    float coc(float z) {
      return smoothstep(uFocus + uFarStart, uFocus + uFarFull, z);
    }

    void main() {
      vec4 base = texture2D(tDiffuse, vUv);
      float c0 = coc(viewZ(vUv));
      if (c0 < 0.01) {
        gl_FragColor = base;
        return;
      }
      vec2 px = 1.0 / uRes;
      float radius = c0 * uMaxRadius;
      vec3 sum = base.rgb;
      float wsum = 1.0;
      // Golden-angle disc. Samples weigh by their own blur, so a sharp
      // foreground (her, close walls) never smears into the background,
      // and by brightness, so lamps bloom into discs.
      for (int i = 1; i < 20; i++) {
        float fi = float(i);
        float rr = sqrt(fi / 19.0) * radius;
        float a = fi * 2.39996;
        vec2 uv = vUv + vec2(cos(a), sin(a)) * rr * px;
        vec3 s = texture2D(tDiffuse, uv).rgb;
        float cs = coc(viewZ(uv));
        float lum = dot(s, vec3(0.299, 0.587, 0.114));
        float w = cs * (1.0 + 3.0 * smoothstep(0.7, 2.5, lum));
        sum += s * w;
        wsum += w;
      }
      gl_FragColor = vec4(sum / wsum, base.a);
    }
  `,
};

class BokehPass extends Pass {
  private readonly quad: ShaderPass;
  constructor(private readonly camera: THREE.PerspectiveCamera) {
    super();
    this.quad = new ShaderPass(BokehShader);
  }

  get uniforms() {
    return this.quad.uniforms;
  }

  setSize(width: number, height: number): void {
    this.quad.uniforms.uRes.value.set(width, height);
  }

  render(
    renderer: THREE.WebGLRenderer,
    writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    const u = this.quad.uniforms;
    u.tDepth.value = readBuffer.depthTexture;
    u.uNear.value = this.camera.near;
    u.uFar.value = this.camera.far;
    this.quad.renderToScreen = this.renderToScreen;
    this.quad.render(renderer, writeBuffer, readBuffer, 0, false);
  }

  dispose(): void {
    this.quad.dispose();
  }
}

/**
 * Ground-truth ambient occlusion at half resolution: soft contact shadows in
 * corners, under eaves, around feet. Additive light effects (beams, halos,
 * pools, rain) aren't surfaces, so they're hidden while it renders normals.
 */
class SoftAOPass extends GTAOPass {
  private readonly hidden: THREE.Object3D[] = [];

  setSize(width: number, height: number): void {
    super.setSize(Math.max(1, Math.floor(width / 2)), Math.max(1, Math.floor(height / 2)));
  }

  render(
    renderer: THREE.WebGLRenderer,
    writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
    deltaTime: number,
    maskActive: boolean,
  ): void {
    const hidden = this.hidden;
    this.scene.traverseVisible((o) => {
      const mat = (o as THREE.Mesh).material as THREE.Material | undefined;
      const fx =
        (o as THREE.Sprite).isSprite ||
        (!!mat && !Array.isArray(mat) && mat.transparent && (!mat.depthWrite || mat.blending === THREE.AdditiveBlending));
      if (fx) hidden.push(o);
    });
    for (const o of hidden) o.visible = false;
    super.render(renderer, writeBuffer, readBuffer, deltaTime, maskActive);
    for (const o of hidden) o.visible = true;
    hidden.length = 0;
  }
}

export class PostFX {
  readonly composer: EffectComposer;
  private readonly bloom: UnrealBloomPass;
  private readonly fear: ShaderPass;
  private readonly crt: ShaderPass;
  private readonly ao: SoftAOPass;
  private readonly bokeh: BokehPass;
  /** Glitch bursts: when the next one starts, how long it lasts, how hard it hits. */
  private glitchNext = 4;
  private glitchEnd = 0;
  private glitchAmp = 0;
  private glitchSeed = 1;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
  ) {
    // The composer's buffers carry a depth texture so the bokeh pass can read scene depth.
    const size = renderer.getSize(new THREE.Vector2());
    const dpr = renderer.getPixelRatio();
    const target = new THREE.WebGLRenderTarget(size.x * dpr, size.y * dpr, { type: THREE.HalfFloatType });
    target.depthTexture = new THREE.DepthTexture(size.x * dpr, size.y * dpr);
    this.composer = new EffectComposer(renderer, target);
    this.composer.addPass(new RenderPass(scene, camera));
    this.bokeh = new BokehPass(camera);
    // Phones and tablets skip it to save fill rate.
    this.bokeh.enabled = !window.matchMedia('(pointer: coarse)').matches;
    this.composer.addPass(this.bokeh);
    this.ao = new SoftAOPass(scene, camera, 512, 512);
    this.ao.updateGtaoMaterial({ radius: 0.8, distanceExponent: 1.4, thickness: 1.2, scale: 1.1, samples: 12, distanceFallOff: 1 });
    this.ao.updatePdMaterial({ lumaPhi: 10, depthPhi: 2, normalPhi: 3, radius: 6, rings: 2, samples: 12 });
    this.ao.blendIntensity = 0.9;
    // Phones and tablets skip it: it costs a full extra scene render.
    this.ao.enabled = !window.matchMedia('(pointer: coarse)').matches;
    this.composer.addPass(this.ao);
    // Big, soft bloom: lamps and signs throw light into the air around them.
    this.bloom = new UnrealBloomPass(new THREE.Vector2(256, 256), 0.6, 0.6, 0.78);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new OutputPass());
    this.fear = new ShaderPass(FearShader);
    this.composer.addPass(this.fear);
    this.crt = new ShaderPass(CrtShader);
    this.composer.addPass(this.crt);
  }

  setSize(width: number, height: number, dpr: number): void {
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(width, height);
    this.fear.uniforms.uRes.value.set(width * dpr, height * dpr);
    this.crt.uniforms.uRes.value.set(width * dpr, height * dpr);
    this.bokeh.uniforms.uMaxRadius.value = 5 * dpr;
    this.crt.uniforms.uDpr.value = dpr;
  }

  update(time: number, fear: number, pulse: number): void {
    this.fear.uniforms.uTime.value = time;
    this.fear.uniforms.uFear.value = fear;
    this.fear.uniforms.uPulse.value = pulse;
    this.crt.uniforms.uTime.value = time;
    this.crt.uniforms.uFear.value = fear;
    this.updateGlitch(time, fear);
  }

  /** Short bursts of tearing every few seconds; more often as she gets scared. */
  private updateGlitch(time: number, fear: number): void {
    const u = this.crt.uniforms;
    if (time >= this.glitchNext) {
      const r = this.rand();
      this.glitchEnd = time + 0.08 + r * 0.3;
      this.glitchAmp = 0.2 + this.rand() * 0.35;
      this.glitchSeed = Math.floor(this.rand() * 1000);
      const gap = THREE.MathUtils.lerp(10, 3, fear);
      this.glitchNext = this.glitchEnd + gap * (0.5 + this.rand());
    }
    const on = time < this.glitchEnd;
    // Burst flickers on and off within itself.
    u.uGlitch.value = on ? this.glitchAmp * (this.rand() < 0.75 ? 1 : 0.25) : 0;
    u.uSeed.value = this.glitchSeed;
  }

  private randState = 1234567;
  private rand(): number {
    this.randState = (this.randState * 1664525 + 1013904223) >>> 0;
    return this.randState / 4294967296;
  }

  /** Distance from the camera that stays sharp (her, roughly). */
  setFocus(distance: number): void {
    this.bokeh.uniforms.uFocus.value = distance;
  }

  setBloom(strength: number): void {
    this.bloom.strength = strength;
  }

  setAo(on: boolean): void {
    this.ao.enabled = on;
  }

  get aoEnabled(): boolean {
    return this.ao.enabled;
  }

  render(): void {
    this.composer.render();
  }

  dispose(): void {
    this.bloom.dispose();
    this.bokeh.dispose();
    this.fear.dispose();
    this.crt.dispose();
    this.ao.dispose();
    this.composer.dispose();
  }
}
